export default class DragSlider {
    constructor(root, options = {}) {
        if (!root) {
            throw new Error("DragSlider requires a root element.");
        }

        this.root = root;
        this.viewport = root.querySelector("[data-slider-viewport]") || root;
        this.track = root.querySelector("[data-slider-track]") || this.viewport.firstElementChild;
        this.slides = Array.from(root.querySelectorAll("[data-slider-slide]"));
        this.dragSurface = this.viewport;
        this.nextButton = root.querySelector("[data-slider-next]");
        this.prevButton = root.querySelector("[data-slider-prev]");
        this.status = root.querySelector("[data-slider-status]");
        this.options = {
            gap: 24,
            desktopCenterWidth: 550,
            desktopSideWidth: 440,
            mobileBreakpoint: 768,
            mobileSidePeek: 0.1,
            uniformSidePeek: 0.5,
            snapThreshold: 0.2,
            snapMinPx: 24,
            snapVelocity: 0.3,
            throwK: 220,
            maxThrowRatio: 0.6,
            startIndex: 1,
            centerHeight: 590,
            sideHeight: 472,
            mobileCenterHeight: null,
            mobileSideHeight: null,
            featuredCount: 2,
            centerClass: "is-center",
            sideClass: "is-side",
            ...options
        };

        this.centerStart = Math.max(0, Math.min(this.options.startIndex, this.slides.length - 1));
        this.dragging = false;
        this.dragStart = 0;
        this.dragX = 0;
        this.lastMoveX = 0;
        this.lastMoveTime = 0;
        this.velocity = 0;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onPrev = this.onPrev.bind(this);
        this.onDragStart = this.onDragStart.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        this.viewport.style.touchAction = "pan-y";
        this.root.addEventListener("dragstart", this.onDragStart);
        this.root.addEventListener("keydown", this.onKeyDown);
        this.dragSurface.addEventListener("pointerdown", this.onPointerDown);
        window.addEventListener("pointermove", this.onPointerMove);
        window.addEventListener("pointerup", this.onPointerUp);
        window.addEventListener("pointercancel", this.onPointerUp);
        window.addEventListener("resize", this.onResize);
        if (this.nextButton) {
            this.nextButton.addEventListener("click", this.onNext);
        }
        if (this.prevButton) {
            this.prevButton.addEventListener("click", this.onPrev);
        }

        this.update();
        requestAnimationFrame(() => this.update());
    }

    destroy() {
        this.root.removeEventListener("dragstart", this.onDragStart);
        this.root.removeEventListener("keydown", this.onKeyDown);
        this.dragSurface.removeEventListener("pointerdown", this.onPointerDown);
        window.removeEventListener("pointermove", this.onPointerMove);
        window.removeEventListener("pointerup", this.onPointerUp);
        window.removeEventListener("pointercancel", this.onPointerUp);
        window.removeEventListener("resize", this.onResize);
        if (this.nextButton) {
            this.nextButton.removeEventListener("click", this.onNext);
        }
        if (this.prevButton) {
            this.prevButton.removeEventListener("click", this.onPrev);
        }
    }

    get viewportWidth() {
        return this.viewport?.getBoundingClientRect().width || 0;
    }

    get isMobile() {
        return this.viewportWidth > 0 && this.viewportWidth < this.options.mobileBreakpoint;
    }

    get centerWidth() {
        if (!this.isMobile && this.uniformHeights) {
            const visibleSlots = this.centerCount === 2 ? 4 : 3;
            const usable = Math.max(0, this.viewportWidth - this.options.gap * (visibleSlots - 1));
            const peek = Math.max(0, this.options.uniformSidePeek);
            const centers = this.centerCount;
            return usable / (centers + peek * 2);
        }
        if (this.isMobile) {
            const usable = Math.max(0, this.viewportWidth - this.options.gap * 2);
            return usable / (1 + this.options.mobileSidePeek * 2);
        }
        const usable = Math.max(0, this.viewportWidth - this.options.gap * 3);
        const centerCount = this.centerCount;
        const total = this.options.desktopCenterWidth * centerCount + this.options.desktopSideWidth * (3 - centerCount);
        return total > 0 ? usable * (this.options.desktopCenterWidth / total) : 0;
    }

    get sideWidth() {
        if (!this.isMobile && this.uniformHeights) {
            return this.centerWidth;
        }
        if (this.isMobile) {
            return this.centerWidth;
        }
        const usable = Math.max(0, this.viewportWidth - this.options.gap * 3);
        const centerCount = this.centerCount;
        const total = this.options.desktopCenterWidth * centerCount + this.options.desktopSideWidth * (3 - centerCount);
        return total > 0 ? usable * (this.options.desktopSideWidth / total) : 0;
    }

    slideWidthFor(index) {
        return this.isCenter(index) ? this.centerWidth : this.sideWidth;
    }

    totalBefore(index) {
        let total = 0;
        for (let i = 0; i < index; i += 1) {
            total += this.slideWidthFor(i) + this.options.gap;
        }
        return total;
    }

    get trackX() {
        if (!this.track) return 0;
        const anchor = this.centerCount === 1
            ? this.totalBefore(this.centerStart) + this.slideWidthFor(this.centerStart) / 2
            : this.totalBefore(this.centerStart) + this.slideWidthFor(this.centerStart) + this.options.gap / 2;
        return this.viewportWidth / 2 - anchor + this.dragX;
    }

    get centerCount() {
        if (this.isMobile) return 1;
        const maxBySlides = this.slides.length <= 3 ? 1 : 2;
        const raw = Number.parseInt(this.options.featuredCount, 10);
        if (Number.isNaN(raw)) return maxBySlides;
        const requested = this.uniformHeights ? 2 : Math.min(2, Math.max(1, raw));
        return Math.min(maxBySlides, requested);
    }

    isCenter(index) {
        if (this.centerCount === 1) {
            return index === this.centerStart;
        }
        return index === this.centerStart || index === this.centerStart + 1;
    }

    clampCenterStart(value) {
        const maxStart = Math.max(0, this.slides.length - this.centerCount);
        return Math.min(maxStart, Math.max(0, value));
    }

    applyWidths() {
        const equalHeight = this.uniformHeights ? this.activeCenterHeight : null;
        this.slides.forEach((slide, index) => {
            slide.style.width = `${this.slideWidthFor(index)}px`;
            slide.classList.toggle(this.options.centerClass, this.isCenter(index));
            slide.classList.toggle(this.options.sideClass, !this.isCenter(index));
            const frame = slide.querySelector("[data-slider-frame]");
            if (frame) {
                const centerHeight = this.activeCenterHeight;
                const sideHeight = this.activeSideHeight;
                frame.style.height = this.isCenter(index)
                    ? `${equalHeight ?? centerHeight}px`
                    : `${equalHeight ?? sideHeight}px`;
            }
        });
    }

    applyViewportHeight() {
        if (!this.viewport) return;
        const maxHeight = Math.max(this.activeCenterHeight, this.activeSideHeight);
        if (Number.isFinite(maxHeight) && maxHeight > 0) {
            this.viewport.style.height = `${maxHeight}px`;
        }
    }

    get activeCenterHeight() {
        if (!this.isMobile) return this.options.centerHeight;
        if (Number.isFinite(this.options.mobileCenterHeight)) return this.options.mobileCenterHeight;
        return this.options.centerHeight;
    }

    get activeSideHeight() {
        if (!this.isMobile) return this.options.sideHeight;
        if (Number.isFinite(this.options.mobileSideHeight)) return this.options.mobileSideHeight;
        return this.options.sideHeight;
    }

    get uniformHeights() {
        const raw = Number.parseInt(this.options.featuredCount, 10);
        return !Number.isNaN(raw) && raw <= 0;
    }

    applyTrack() {
        if (!this.track) return;
        this.track.style.transform = `translateX(${this.trackX}px)`;
        this.track.style.transition = this.dragging ? "none" : "transform 500ms ease-out";
    }

    update() {
        this.centerStart = this.clampCenterStart(this.centerStart);
        if (this.root && !this.root.hasAttribute("tabindex")) {
            this.root.setAttribute("tabindex", "0");
        }
        if (this.track) {
            this.track.style.gap = `${this.options.gap}px`;
        }
        this.applyViewportHeight();
        this.applyWidths();
        this.applyTrack();
        this.applyAria();
    }

    onPointerDown(event) {
        if (!this.track) return;
        if (event.target?.closest?.("a, button, input, select, textarea, summary, [role='button']")) return;
        if (event.target?.closest?.("[data-slider-next], [data-slider-prev]")) return;
        if (event.button !== undefined && event.button !== 0) return;
        event.preventDefault();
        this.dragging = true;
        this.dragStart = event.clientX;
        this.dragX = 0;
        this.lastMoveX = event.clientX;
        this.lastMoveTime = performance.now();
        this.velocity = 0;
        if (this.viewport?.setPointerCapture) {
            this.viewport.setPointerCapture(event.pointerId);
        }
    }

    onPointerMove(event) {
        if (!this.dragging) return;
        const now = performance.now();
        const dx = event.clientX - this.lastMoveX;
        const dt = now - this.lastMoveTime;
        if (dt > 0) {
            this.velocity = dx / dt;
        }
        this.lastMoveX = event.clientX;
        this.lastMoveTime = now;
        this.dragX = event.clientX - this.dragStart;
        this.applyTrack();
    }

    onPointerUp() {
        if (!this.dragging) return;
        this.dragging = false;
        const step = (this.isMobile ? this.centerWidth : this.sideWidth) + this.options.gap;
        const distance = -this.dragX;
        const absDistance = Math.abs(distance);
        const absVelocity = Math.abs(this.velocity);
        const thresholdByStep = this.options.snapThreshold * step;
        const minDistance = Math.max(this.options.snapMinPx, thresholdByStep);
        const shouldSnap = absDistance >= minDistance || absVelocity >= this.options.snapVelocity;
        let shift = 0;
        if (shouldSnap && step > 0) {
            // Always move at most one slide per gesture.
            const direction = Math.sign(distance) || Math.sign(this.velocity);
            shift = direction;
        }
        this.centerStart = this.clampCenterStart(this.centerStart + shift);
        this.dragX = 0;
        this.velocity = 0;
        this.applyTrack();
        this.applyWidths();
    }

    onResize() {
        this.update();
    }

    onDragStart(event) {
        event.preventDefault();
    }

    applyAria() {
        const total = this.slides.length;
        this.slides.forEach((slide, index) => {
            const isCenter = this.isCenter(index);
            slide.setAttribute("aria-hidden", isCenter ? "false" : "true");
            slide.setAttribute("aria-roledescription", "slide");
            slide.setAttribute("aria-setsize", `${total}`);
            slide.setAttribute("aria-posinset", `${index + 1}`);
        });

        if (this.status) {
            const centerIndex = this.centerStart;
            const slide = this.slides[centerIndex];
            const label = slide?.getAttribute("aria-label") || `Slide ${centerIndex + 1} of ${total}`;
            this.status.textContent = label;
        }
    }

    onKeyDown(event) {
        if (event.altKey || event.metaKey || event.ctrlKey) return;
        switch (event.key) {
            case "ArrowRight":
            case "PageDown":
                event.preventDefault();
                this.onNext();
                break;
            case "ArrowLeft":
            case "PageUp":
                event.preventDefault();
                this.onPrev();
                break;
            case "Home":
                event.preventDefault();
                this.centerStart = this.clampCenterStart(0);
                this.update();
                break;
            case "End":
                event.preventDefault();
                this.centerStart = this.clampCenterStart(this.slides.length - 1);
                this.update();
                break;
            default:
                break;
        }
    }

    onNext() {
        this.centerStart = this.clampCenterStart(this.centerStart + 1);
        this.update();
    }

    onPrev() {
        this.centerStart = this.clampCenterStart(this.centerStart - 1);
        this.update();
    }
}

DragSlider.parseOptions = function (root) {
    const options = {};
    if (!root?.dataset) return options;

    const json = root.dataset.sliderOptions;
    if (json) {
        try {
            Object.assign(options, JSON.parse(json));
        } catch (error) {
            console.warn("DragSlider: invalid data-slider-options JSON.", error);
        }
    }

    const numberMap = {
        sliderGap: "gap",
        sliderDesktopCenterWidth: "desktopCenterWidth",
        sliderDesktopSideWidth: "desktopSideWidth",
        sliderMobileBreakpoint: "mobileBreakpoint",
        sliderMobileSidePeek: "mobileSidePeek",
        sliderUniformSidePeek: "uniformSidePeek",
        sliderSnapThreshold: "snapThreshold",
        sliderSnapMinPx: "snapMinPx",
        sliderSnapVelocity: "snapVelocity",
        sliderThrowK: "throwK",
        sliderMaxThrowRatio: "maxThrowRatio",
        sliderStartIndex: "startIndex",
        sliderCenterHeight: "centerHeight",
        sliderSideHeight: "sideHeight",
        sliderMobileCenterHeight: "mobileCenterHeight",
        sliderMobileSideHeight: "mobileSideHeight",
        sliderFeaturedCount: "featuredCount"
    };

    Object.keys(numberMap).forEach((key) => {
        if (root.dataset[key] === undefined) return;
        const value = Number.parseFloat(root.dataset[key]);
        if (!Number.isNaN(value)) {
            options[numberMap[key]] = value;
        }
    });

    return options;
};

if (typeof window !== "undefined") {
    window.DragSlider = DragSlider;
}

if (typeof document !== "undefined") {
    document.addEventListener("alpine:init", () => {
        if (!window.Alpine) return;
        window.Alpine.data("dragSlider", (options = {}) => ({
            init() {
                const dataOptions = DragSlider.parseOptions(this.$el);
                this.dragSlider = new DragSlider(this.$el, {
                    ...dataOptions,
                    ...options
                });
            },
            destroy() {
                this.dragSlider?.destroy();
            }
        }));
    });
}
